import React from 'react';
import './deep-dives-styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import c1 from '../assets/cloud9-1.png'
import c2 from '../assets/cloud9-2.png'
import c3 from '../assets/cloud9-3.png'
import c4 from '../assets/cloud9-4.png'
import Header from '../components/header';
import GoToTop from '../components/go-to-top';
import Articol from '../components/article';

class Cloud9 extends React.Component {

    componentDidMount() {
        window.scrollTo(0,0);
    }

    render() {

        return (
            <div>
                <Header />

                <GoToTop />
                <div className='page'>
                    <title>Debugging a delete error for AWS Cloud9 environment</title>
                    <h1 className='title'>Debugging a delete error for AWS Cloud9 environment</h1>
                    <div className='paragraph'>
                        Updated on 21 May 2025
                    </div>

                    <div className='paragraph'>The issue I want to explore in this 
                        post is one I am receiving when trying to delete an AWS Cloud9 
                        environment in AWS. Because I started working on the solution at 
                        a later time after the first occurrence, I found that a good 
                        starting point is the CloudFormation service.
                    </div>

                    <h2 className='dd-subtitle'>AWS CloudFormation</h2>
                    <div className='paragraph'>
                        <strong>AWS CloudFormation</strong> is primarily a tool for Infrastructure as Code (IaC), 
                        meaning you define your infrastructure in templates (YAML or JSON) and 
                        then explicitly create or update stacks based on those templates. AWS 
                        CloudFormation is a service that helps you model and set up your <strong>Amazon 
                        Web Services resources</strong>. It allows you to create and manage a collection 
                        of related AWS resources as a single unit called a <strong>stack</strong>.
                    </div>

                    <h2 className='dd-subtitle'>Inside CloudFormation</h2>
                    <div className='paragraph'>
                       In the AWS web interface, go to the CloudFormation service. In the “Stacks” 
                       list I can easily find the row showing DELETE_FAILED error. Each of these stacks 
                       correspond to a resource I am using. The stack showing this error represents 
                       the Cloud9 environment I am trying to delete.<br/>
                       
                       Although in most cases the user is initiating the creation of a Stack, in 
                       this case Cloud9 AWS service used CloudFormation internally to manage its 
                       own infrastructure to provision required resources such as the EC2 instance and 
                       the security group. This is typically abstracted away from you, and you don't 
                       directly interact with the CloudFormation stack in these cases.
                    </div>
                    
                    
                    <div className='paragraph'>
                        Clicking on the corresponding “Stack” and going to the “Events” tab, I can already see some more details about the source of this error:
                        
                        <div className='issue mt-2'>
                            <div className='issue_title'><FontAwesomeIcon className='issue-red' icon={faTriangleExclamation}/> DELETE_FAILED </div>
                            This AWS::EC2::SecurityGroup resource is in a DELETE_FAILED state.<br/>
                            resource sg-08353*** has a dependent object (Service: Ec2, Status Code: 400, Request ID: db914784-2ef4-42e9-8fe0-a2dec9f77830) (SDK Attempt Count: 1)
                        </div>
                    </div>

                    <h2 className='dd-subtitle'>1. CloudFormation Events</h2>
                    <div className='paragraph'>
                        Looking at the events list, it is also confirming that the 
                        item marked with “Likely root cause” is showing the same 
                        error. I can understand from the events list that after 
                        the user initiates the delete process, the first thing AWS 
                        is doing is to delete the security group associated with 
                        the EC2 instance used to run the Cloud9 service.
                    </div>


                    <div className='paragraph'>
                        I can understand from this error that the EC2 instance 
                        is using the security group with id sg-08353a41c5ee67d9e 
                        and that security group cannot be deleted.
                    </div>
                    
                    
                    <h2 className='dd-subtitle'>1.1. The security group</h2>
                    <div className='paragraph'>
                        Notice that looking at the security group details page, 
                        at the VPC Associations tab, it shows no available items. 
                        If a resource would depend on this security group one would 
                        expect to show up here.
                    </div>
                    
                    
                    <h2 className='dd-subtitle'>1.1 Security group delete</h2>
                    <div className='paragraph'>
                        If trying to delete the security group from here, like this:
                    </div>
                    
                    <img alt='Delete security group operation' className='img-row' src={c1} />
                    
                    <div className='paragraph'>
                        I get the following error:
                    </div>
                    
                    <img alt='Delete security group error' className='img-row' src={c2} />
                    
                    <div className='paragraph'>
                        Looking at this error I can identify four distinct elements that I need to investigate: <br/>
                        - Is a default security group <br/>
                        - Is associated with other instances <br/>
                        - Is associated with network interfaces <br/>
                        - Is referenced by other security groups <br/>
                    </div>
                    
                    <div className='paragraph'> Let’s take each one: </div>
                    
                    <h6>1. Is a default security group</h6>
                    <div className='paragraph'>
                        A <strong>default security group</strong> is that security 
                        group that is automatically created when the resource you 
                        requested is created. From this security group details tab, 
                        you can go to the VPC dashboard screen. Then, we select the 
                        “Security groups” section:
                    </div>
                    
                    <img alt='Security group details' className='img-row md' src={c3} />
                    
                    <div className='paragraph'>
                        You can see that the security group we are concerned about, 
                        is not a default security group but a security group created 
                        for AWS Cloud9 environment with the identification 'aws-cloud9-MyCloud9Env-8db35e...'.
                    </div>
                    
                    
                    <h6>2. Is associated with other instances</h6>
                    <div className='paragraph'>
                        Hoping that any associations would appear in the “VPC associations 
                        - new” tab, I don’t see any reasons to think that is associated 
                        with other instances.
                    </div>
                    
                    <h6>3. Is associated with network interfaces</h6>
                    <div className='paragraph'>
                        A <strong>network interface</strong> is a virtual networking 
                        component that represents a virtual network card within a 
                        Virtual Private Cloud (VPC). Each network interface has a 
                        primary private IPv4 address.
                    </div>
                    
                    <div className='side-story'>
                        <h2>Side story: CIDR</h2>

                        <div className='paragraph'>
                            <strong>CIDR</strong> stands for Classless Inter-Domain 
                            Routing. It is a method for allocating IP addresses and 
                            routing Internet Protocol (IP) packets more efficiently 
                            than the older classful (Class A, B, C) addressing system. 
                            It allows for flexible subnet sizes, reducing IP address 
                            waste and improving scalability in networks, including AWS VPCs.
                        </div>
                    </div>
                    
                    <div className='paragraph'>
                        From the AWS Cloud9 service, Network settings, I can see that it is part of the subnet identified by subnet-06bdf632e***.
                    </div>
                    
                    
                    <h6>4. Is referenced by other security groups</h6>
                    <div className='paragraph'>
                        If we analyse the relations starting from the target resource 
                        we want to delete, the AWS Cloud9 environment, we can see 
                        that it is part of the subnet identified by subnet-06bdf6… 
                        This subnet is part of the VPC identified by vpc-06d516… 
                        The VPC has 3 security groups: a default one, one created 
                        for the Cloud9 environment (which shows up in the error), 
                        and another one called MyClusterGroup.
                    </div>
                    
                    <div className='paragraph'>
                        Using the filter option to search from all the available 
                        security groups by the id of the security group to be deleted, 
                        it finds 2 items. On the second item, you can see that the 
                        security group with id sg-08353a41c5ee67… appears in the 
                        “Inbound rules” section of the second item found. This 
                        looks like an association that could lead to our error:
                    </div>
                    
                    <img alt='Security group inbound rules' className='img-row md' src={c4} />
                    
                    
                    <div className='paragraph'>
                        After deleting the rule containing the security group with 
                        id sg-08353a41c5ee67…, the Cloud9 environment was successfully 
                        deleted making this the happy ending of this post.
                    </div>
                

                    <h2 className='dd-subtitle'>Final thoughts</h2>
                    <div className='paragraph'>
                        Search function provided by the AWS web interface proved to be a
                        a very valuable resource. Even though it was not obvious to meaning
                        why there were 2 results displayed, by digging into the details
                        of the second security group the solution became immediately
                        clear
                    </div>


                    <div className='articles mt-5'>
                        <Articol title='Hosted React to AWS Amplify' to={'/dp-dv-react-to-aws'} />
                        <Articol title='GoLang Web app on AWS (Part 2) - Front-end and CORS' to={'/dp-golang-webapp-cors'} />
                    </div>

                    <div className='mb-10'></div>

                {/* Closing page  */}
                </div>

            {/* Closing first dev  */}
            </div>
        )
    }
}

export default Cloud9;